import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import { ControlPogResetStatsResponse } from '../../types';

const percent = (part: number, total: number): string =>
  total > 0 ? `${Math.round((part / total) * 100)}%` : '—';

const BarRow: React.FC<{ label: string; value: number; total: number; color: string }> = ({
  label,
  value,
  total,
  color,
}) => (
  <div className="flex items-center gap-3 text-sm">
    <span className="w-40 text-gray-600">{label}</span>
    <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: total > 0 ? `${(value / total) * 100}%` : 0 }}
      />
    </div>
    <span className="w-24 text-right tabular-nums">
      {value} <span className="text-gray-400">({percent(value, total)})</span>
    </span>
  </div>
);

const StatCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow-md p-4">
    <h2 className="text-sm font-semibold text-gray-700 mb-3">{title}</h2>
    <div className="space-y-2">{children}</div>
  </div>
);

export const PogResetStatsPage: React.FC = () => {
  const { storeName } = useParams<{ storeName: string }>();
  const { token } = useAuthContext();
  const { toggleLoading, showErrorToast } = useNotificationContext();

  const [stats, setStats] = useState<ControlPogResetStatsResponse | null>(null);

  useEffect(() => {
    if (!storeName) return undefined;
    let active = true;
    const load = async () => {
      toggleLoading(true);
      try {
        const response = await ApiService.fetchPogResetStats(token, storeName);
        if (!active) return;
        setStats(response);
      } catch (err) {
        if (!active) return;
        showErrorToast(err instanceof Error ? err.message : 'Error loading statistics');
      } finally {
        if (active) toggleLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, storeName]);

  if (!stats) {
    return <div className="min-h-screen bg-gray-100 p-6" />;
  }

  const { taskAccuracy, modAccuracy, sequence, proposal, scanVsPr, scoreBuckets, misses } = stats;
  const proposalTotal = proposal.proposalCorrect + proposal.overridden + proposal.noProposal;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link
            to={`/stores/${storeName}/pog-reset`}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded-md"
            title="Back to reset tasks"
          >
            <ArrowBackIcon />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Mapping accuracy — confirmed tasks</h1>
            <p className="text-sm text-gray-600">
              {stats.tasksTotal} confirmed tasks · {stats.modsTotal} mods · computed candidates vs
              what reviewers actually applied
            </p>
          </div>
        </div>

        {stats.tasksTotal === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No confirmed mappings yet — statistics appear after reset tasks are resolved through
            this tool.
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const tier = scoreBuckets.find((bucket) => bucket.minScore === 0.9);
              if (!tier || tier.mods === 0) return null;
              return (
                <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
                  <h2 className="text-sm font-semibold text-gray-700">
                    Auto-apply tier — proposals with score ≥ 90%
                  </h2>
                  <p className="text-2xl font-bold mt-1">
                    {tier.proposalCorrect}/{tier.mods} = {percent(tier.proposalCorrect, tier.mods)}{' '}
                    matched the reviewer
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    These mods would have been mapped correctly with no human input. When this holds
                    at ~100%, the tier is safe to auto-work.
                  </p>
                </div>
              );
            })()}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard title="Tasks by candidate rank needed (all mods correct within top-N)">
                <BarRow
                  label="Top-1 (fully automatic)"
                  value={taskAccuracy.top1}
                  total={stats.tasksTotal}
                  color="bg-green-500"
                />
                <BarRow
                  label="Saved by top-2"
                  value={taskAccuracy.top2}
                  total={stats.tasksTotal}
                  color="bg-lime-500"
                />
                <BarRow
                  label="Saved by top-3"
                  value={taskAccuracy.top3}
                  total={stats.tasksTotal}
                  color="bg-yellow-500"
                />
                <BarRow
                  label="Needed manual input"
                  value={taskAccuracy.manual}
                  total={stats.tasksTotal}
                  color="bg-red-400"
                />
              </StatCard>

              <StatCard title="Mods by matching candidate rank">
                <BarRow
                  label="Rank 1"
                  value={modAccuracy.rank1}
                  total={stats.modsTotal}
                  color="bg-green-500"
                />
                <BarRow
                  label="Rank 2"
                  value={modAccuracy.rank2}
                  total={stats.modsTotal}
                  color="bg-lime-500"
                />
                <BarRow
                  label="Rank 3"
                  value={modAccuracy.rank3}
                  total={stats.modsTotal}
                  color="bg-yellow-500"
                />
                <BarRow
                  label="Manual (PR enabled)"
                  value={modAccuracy.manual - modAccuracy.manualPrDisabled}
                  total={stats.modsTotal}
                  color="bg-red-400"
                />
                <BarRow
                  label="Manual (PR off — expected)"
                  value={modAccuracy.manualPrDisabled}
                  total={stats.modsTotal}
                  color="bg-gray-300"
                />
                <BarRow
                  label="Marked missing"
                  value={modAccuracy.missing}
                  total={stats.modsTotal}
                  color="bg-gray-400"
                />
              </StatCard>

              <StatCard title="Applied tag sequence per task">
                <BarRow
                  label="Consecutive run"
                  value={sequence.consecutive}
                  total={stats.tasksTotal}
                  color="bg-green-500"
                />
                <BarRow
                  label="Gapped (same aisle)"
                  value={sequence.gapped}
                  total={stats.tasksTotal}
                  color="bg-yellow-500"
                />
                <BarRow
                  label="Fragmented (mixed idents)"
                  value={sequence.fragmented}
                  total={stats.tasksTotal}
                  color="bg-red-400"
                />
                <BarRow
                  label="Single-mod task"
                  value={sequence.single}
                  total={stats.tasksTotal}
                  color="bg-gray-400"
                />
              </StatCard>

              <StatCard title="Proposal outcome per mod">
                <BarRow
                  label="Accepted as proposed"
                  value={proposal.proposalCorrect}
                  total={proposalTotal}
                  color="bg-green-500"
                />
                <BarRow
                  label="Overridden by reviewer"
                  value={proposal.overridden}
                  total={proposalTotal}
                  color="bg-red-400"
                />
                <BarRow
                  label="No proposal (PR enabled)"
                  value={proposal.noProposal - proposal.noProposalPrDisabled}
                  total={proposalTotal}
                  color="bg-amber-400"
                />
                <BarRow
                  label="No proposal (PR off — expected)"
                  value={proposal.noProposalPrDisabled}
                  total={proposalTotal}
                  color="bg-gray-300"
                />
              </StatCard>
            </div>

            {scanVsPr.scanTotal > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-sm font-semibold text-gray-700 mb-1">
                  Field scan (FT) vs product recognition (PR)
                </h2>
                <p className="text-xs text-gray-500 mb-3">
                  FT = newest install-scan option per mod; PR = rank-1 camera candidate. Accuracy is
                  measured against the tag the reviewer actually applied.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">Agreement (both present)</div>
                    <div className="text-lg font-bold tabular-nums">
                      {scanVsPr.agree}/{scanVsPr.bothPresent} ={' '}
                      {percent(scanVsPr.agree, scanVsPr.bothPresent)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">FT top-1 accuracy</div>
                    <div className="text-lg font-bold tabular-nums">
                      {scanVsPr.scanCorrect}/{scanVsPr.scanTotal} ={' '}
                      {percent(scanVsPr.scanCorrect, scanVsPr.scanTotal)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">PR top-1 accuracy</div>
                    <div className="text-lg font-bold tabular-nums">
                      {scanVsPr.prCorrect}/{scanVsPr.prTotal} ={' '}
                      {percent(scanVsPr.prCorrect, scanVsPr.prTotal)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Applied via scan</div>
                    <div className="text-lg font-bold tabular-nums">{scanVsPr.appliedFromScan}</div>
                  </div>
                </div>
                {scanVsPr.disagreements > 0 && (
                  <p className="text-sm text-gray-600 mt-3">
                    Disagreements: {scanVsPr.disagreements} — FT right{' '}
                    <span className="font-medium">{scanVsPr.disagreeScanCorrect}</span>, PR right{' '}
                    <span className="font-medium">{scanVsPr.disagreePrCorrect}</span>, neither{' '}
                    <span className="font-medium">
                      {scanVsPr.disagreements -
                        scanVsPr.disagreeScanCorrect -
                        scanVsPr.disagreePrCorrect}
                    </span>
                  </p>
                )}
              </div>
            )}

            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-1">
                Auto-apply simulation — proposal accuracy by score threshold
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                If proposals were auto-applied only when their score is at least the threshold: how
                many mods qualify and how often the proposal matched what the reviewer applied.
              </p>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1 pr-6 font-medium">Score ≥</th>
                    <th className="py-1 pr-6 font-medium">Mods qualifying</th>
                    <th className="py-1 pr-6 font-medium">Proposal correct</th>
                    <th className="py-1 font-medium">Accuracy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {scoreBuckets.map((bucket) => (
                    <tr key={bucket.minScore}>
                      <td className="py-1.5 pr-6 tabular-nums">
                        {Math.round(bucket.minScore * 100)}%
                      </td>
                      <td className="py-1.5 pr-6 tabular-nums">{bucket.mods}</td>
                      <td className="py-1.5 pr-6 tabular-nums">{bucket.proposalCorrect}</td>
                      <td className="py-1.5 tabular-nums font-medium">
                        {percent(bucket.proposalCorrect, bucket.mods)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-1">
                Hard failures — applied tag not in candidates ({misses.length})
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                The mods the algorithm could not have gotten right at any rank — study these to
                improve matching.
              </p>
              {misses.length === 0 ? (
                <p className="text-sm text-gray-400">None — every applied tag was a candidate.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="py-1 pr-4 font-medium">Task</th>
                      <th className="py-1 pr-4 font-medium">POG</th>
                      <th className="py-1 pr-4 font-medium">Mod #</th>
                      <th className="py-1 pr-4 font-medium">Applied tag</th>
                      <th className="py-1 pr-4 font-medium">Proposed</th>
                      <th className="py-1 font-medium">Candidates</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {misses.map((miss) => (
                      <tr key={`${miss.taskId}-${miss.modNumber}`}>
                        <td className="py-1.5 pr-4">
                          <Link
                            to={`/stores/${storeName}/pog-reset/${miss.taskId}`}
                            className="text-blue-600 hover:underline"
                          >
                            {miss.taskId}
                          </Link>
                        </td>
                        <td className="py-1.5 pr-4">{miss.pogName ?? '—'}</td>
                        <td className="py-1.5 pr-4 tabular-nums">{miss.modNumber ?? '—'}</td>
                        <td className="py-1.5 pr-4 font-mono whitespace-nowrap">
                          {miss.appliedTag}
                        </td>
                        <td className="py-1.5 pr-4 font-mono whitespace-nowrap">
                          {miss.proposedTag ?? '—'}
                          {miss.proposedScore != null && (
                            <span className="text-gray-400 font-sans">
                              {' '}
                              ({Math.round(miss.proposedScore * 100)}%)
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 font-mono text-xs">
                          {miss.candidateTags.join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
